import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { Card, Col, Row, Form, Button, Alert, Spinner } from 'react-bootstrap';
import ParticlesAuth from './ParticlesAuth';
import PhoneCountryInput from '../common/PhoneCountryInput';
import { register } from '../../features/auth/authSlice';

const COMPANY_SIZE_OPTIONS = [
  { value: '1', label: '1' },
  { value: '2-4', label: '2–4' },
  { value: '4-10', label: '4–10' },
  { value: '11-20', label: '11–20' },
  { value: '21-50', label: '21–50' },
  { value: '51-100', label: '51–100' },
  { value: '100+', label: '100+' },
];

const STEP1_FIELDS = ['name', 'email', 'phone', 'password', 'password_confirmation'];

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, token, status, error: authError } = useSelector((state) => state.auth);

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    phone: '',
    company_name: '',
    company_size: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_postal_code: '',
    address_country: '',
    user_tier: 'business',
  });

  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordShow, setPasswordShow] = useState(false);
  const [confirmPasswordShow, setConfirmPasswordShow] = useState(false);

  useEffect(() => {
    if (user && token) {
      const needsVerify = localStorage.getItem('auth_verification_required') === 'true';
      navigate(needsVerify ? '/verify-email' : '/dashboard', { replace: true });
    }
  }, [user, token, navigate]);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validateStep1 = () => {
    const clientErrors = {};
    if (!String(formData.name || '').trim()) {
      clientErrors.name = ['Full name is required.'];
    }
    if (!String(formData.email || '').trim()) {
      clientErrors.email = ['Email is required.'];
    }
    if (!String(formData.phone || '').trim()) {
      clientErrors.phone = ['Phone is required.'];
    }
    if (!String(formData.password || '').trim()) {
      clientErrors.password = ['Password is required.'];
    } else if (formData.password.length < 8) {
      clientErrors.password = ['Password must be at least 8 characters.'];
    }
    if (formData.password !== formData.password_confirmation) {
      clientErrors.password_confirmation = ['Passwords do not match'];
    }
    return clientErrors;
  };

  const validateStep2 = () => {
    const clientErrors = {};
    if (!String(formData.company_name || '').trim()) {
      clientErrors.company_name = ['Company name is required.'];
    }
    if (!String(formData.company_size || '').trim()) {
      clientErrors.company_size = ['Company size is required.'];
    }
    return clientErrors;
  };

  const advanceToStep2 = () => {
    setError('');
    const clientErrors = validateStep1();
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      setError('Please complete all required account fields.');
      return false;
    }
    setErrors({});
    setStep(2);
    return true;
  };

  const goToStep2 = (e) => {
    e.preventDefault();
    advanceToStep2();
  };

  const handleStepTab = (targetStep) => {
    if (targetStep === 1) {
      setStep(1);
      return;
    }
    if (targetStep === 2) {
      if (step === 2) return;
      advanceToStep2();
    }
  };

  const focusStepForErrors = (errorMap) => {
    if (Object.keys(errorMap).some((key) => STEP1_FIELDS.includes(key))) {
      setStep(1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrors({});
    setSuccess('');

    const step1Errors = validateStep1();
    const step2Errors = validateStep2();
    const clientErrors = { ...step1Errors, ...step2Errors };

    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      setError('Please fill in all required fields.');
      focusStepForErrors(clientErrors);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const result = await dispatch(register(formData)).unwrap();
      if (result && (result.token || result.verificationRequired)) {
        setSuccess(
          result.message ||
            'Account created. Check your email for a verification code.'
        );
        navigate('/verify-email', { replace: true });
      } else if (result && result.message) {
        setSuccess(result.message);
        navigate('/verify-email', { replace: true });
      }
    } catch (err) {
      if (typeof err === 'string') {
        setError(err);
        setErrors({});
      } else if (err && typeof err === 'object') {
        setError(err.message || 'Registration failed. Please check your details and try again.');
        const apiErrors = err.errors || {};
        setErrors(apiErrors);
        focusStepForErrors(apiErrors);
      } else {
        setError('Registration failed. Please check your details and try again.');
        setErrors({});
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (field) =>
    `form-control bg-light border-light ${errors[field] ? 'is-invalid' : ''}`;

  const renderPasswordToggle = (show, setShow) => (
    <button
      className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted password-addon"
      type="button"
      onClick={() => setShow(!show)}
      tabIndex={-1}
    >
      <i className={show ? 'ri-eye-off-fill align-middle' : 'ri-eye-fill align-middle'} />
    </button>
  );

  return (
    <ParticlesAuth showPromoPanel={false}>
      <Col xxl={10} lg={11} className="mx-auto">
        <Card className="mb-0 border-0 shadow-none mb-0">
          <Card.Body className="p-sm-5">
            <div className="text-center mt-3 mt-sm-5">
              <h5 className="fs-3xl">Create Your Account</h5>
              <p className="text-muted mb-0">Sign up to start using Volksign.</p>
            </div>

            <ul className="nav nav-pills step-arrow-nav mb-4 mt-4" role="tablist">
              <li className="nav-item flex-fill">
                <button
                  type="button"
                  className={`nav-link w-100 ${step === 1 ? 'active' : step > 1 ? 'done' : ''}`}
                  onClick={() => handleStepTab(1)}
                  aria-current={step === 1 ? 'step' : undefined}
                >
                  <span className="d-none d-sm-inline">Step 1 — </span>Account
                </button>
              </li>
              <li className="nav-item flex-fill">
                <button
                  type="button"
                  className={`nav-link w-100 ${step === 2 ? 'active' : ''}`}
                  onClick={() => handleStepTab(2)}
                  aria-current={step === 2 ? 'step' : undefined}
                >
                  <span className="d-none d-sm-inline">Step 2 — </span>Company
                </button>
              </li>
            </ul>

            <div className="p-2">
              {(error || authError) && (
                <Alert variant="danger" className="mb-3">
                  <div>
                    {error ||
                      (typeof authError === 'string'
                        ? authError
                        : authError?.message || 'An error occurred')}
                  </div>
                  {(Object.keys(errors).length > 0 ||
                    (authError && typeof authError === 'object' && authError.errors)) && (
                    <ul className="mb-0 mt-2" style={{ paddingLeft: '20px' }}>
                      {Object.entries(errors || authError?.errors || {}).map(
                        ([field, messages]) => (
                          <li key={field}>
                            <strong>{field.replace(/_/g, ' ')}:</strong>{' '}
                            {Array.isArray(messages) ? messages.join(', ') : messages}
                          </li>
                        )
                      )}
                    </ul>
                  )}
                </Alert>
              )}
              {success && (
                <Alert variant="success" className="mb-3">
                  {success}
                </Alert>
              )}

              <Form onSubmit={step === 1 ? goToStep2 : handleSubmit}>
                {step === 1 && (
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group controlId="name">
                        <Form.Label>
                          Full Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          className={inputClass('name')}
                          placeholder="Enter your name"
                          value={formData.name}
                          onChange={(e) => updateField('name', e.target.value)}
                          required
                        />
                        {errors.name && (
                          <Form.Control.Feedback type="invalid" className="d-block">
                            {Array.isArray(errors.name) ? errors.name[0] : errors.name}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="email">
                        <Form.Label>
                          Email <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          className={inputClass('email')}
                          placeholder="Enter email"
                          value={formData.email}
                          onChange={(e) => updateField('email', e.target.value)}
                          required
                        />
                        {errors.email && (
                          <Form.Control.Feedback type="invalid" className="d-block">
                            {Array.isArray(errors.email) ? errors.email[0] : errors.email}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group controlId="phone">
                        <Form.Label>
                          Phone <span className="text-danger">*</span>
                        </Form.Label>
                        <PhoneCountryInput
                          id="phone"
                          value={formData.phone}
                          className="bg-light border-light"
                          placeholder="Enter phone number"
                          isInvalid={!!errors.phone}
                          required
                          onChange={(phone) => updateField('phone', phone)}
                        />
                        {errors.phone && (
                          <Form.Control.Feedback type="invalid" className="d-block">
                            {Array.isArray(errors.phone) ? errors.phone[0] : errors.phone}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="password">
                        <Form.Label>
                          Password <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="position-relative auth-pass-inputgroup">
                          <Form.Control
                            type={passwordShow ? 'text' : 'password'}
                            name="password"
                            className={`${inputClass('password')} pe-5 password-input`}
                            placeholder="Enter password"
                            autoComplete="new-password"
                            value={formData.password}
                            onChange={(e) => updateField('password', e.target.value)}
                            required
                            minLength={8}
                          />
                          {renderPasswordToggle(passwordShow, setPasswordShow)}
                        </div>
                        {errors.password && (
                          <Form.Control.Feedback type="invalid" className="d-block">
                            {Array.isArray(errors.password) ? errors.password[0] : errors.password}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="password_confirmation">
                        <Form.Label>
                          Confirm Password <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="position-relative auth-pass-inputgroup">
                          <Form.Control
                            type={confirmPasswordShow ? 'text' : 'password'}
                            name="password_confirmation"
                            className={`${inputClass('password_confirmation')} pe-5 password-input`}
                            placeholder="Confirm password"
                            autoComplete="new-password"
                            value={formData.password_confirmation}
                            onChange={(e) =>
                              updateField('password_confirmation', e.target.value)
                            }
                            required
                            minLength={8}
                          />
                          {renderPasswordToggle(confirmPasswordShow, setConfirmPasswordShow)}
                        </div>
                        {errors.password_confirmation && (
                          <Form.Control.Feedback type="invalid" className="d-block">
                            {Array.isArray(errors.password_confirmation)
                              ? errors.password_confirmation[0]
                              : errors.password_confirmation}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Button className="btn btn-primary w-100" type="submit">
                        Continue
                        <i className="ri-arrow-right-line align-middle ms-1" />
                      </Button>
                    </Col>
                  </Row>
                )}

                {step === 2 && (
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group controlId="company_name">
                        <Form.Label>
                          Company Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="company_name"
                          className={inputClass('company_name')}
                          placeholder="Enter company name"
                          value={formData.company_name}
                          onChange={(e) => updateField('company_name', e.target.value)}
                          required
                        />
                        {errors.company_name && (
                          <Form.Control.Feedback type="invalid" className="d-block">
                            {Array.isArray(errors.company_name)
                              ? errors.company_name[0]
                              : errors.company_name}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="company_size">
                        <Form.Label>
                          Company Size <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          name="company_size"
                          className={inputClass('company_size')}
                          value={formData.company_size}
                          onChange={(e) => updateField('company_size', e.target.value)}
                          required
                        >
                          <option value="">Select company size</option>
                          {COMPANY_SIZE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Form.Select>
                        {errors.company_size && (
                          <Form.Control.Feedback type="invalid" className="d-block">
                            {Array.isArray(errors.company_size)
                              ? errors.company_size[0]
                              : errors.company_size}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group controlId="address_street">
                        <Form.Label>Street Address</Form.Label>
                        <Form.Control
                          type="text"
                          name="address_street"
                          className="form-control bg-light border-light"
                          placeholder="123 Main St"
                          value={formData.address_street}
                          onChange={(e) => updateField('address_street', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group controlId="address_city">
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          type="text"
                          name="address_city"
                          className="form-control bg-light border-light"
                          placeholder="City"
                          value={formData.address_city}
                          onChange={(e) => updateField('address_city', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group controlId="address_state">
                        <Form.Label>State</Form.Label>
                        <Form.Control
                          type="text"
                          name="address_state"
                          className="form-control bg-light border-light"
                          placeholder="State"
                          value={formData.address_state}
                          onChange={(e) => updateField('address_state', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group controlId="address_postal_code">
                        <Form.Label>Postal Code</Form.Label>
                        <Form.Control
                          type="text"
                          name="address_postal_code"
                          className="form-control bg-light border-light"
                          placeholder="Postal Code"
                          value={formData.address_postal_code}
                          onChange={(e) =>
                            updateField('address_postal_code', e.target.value)
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="address_country">
                        <Form.Label>Country</Form.Label>
                        <Form.Control
                          type="text"
                          name="address_country"
                          className="form-control bg-light border-light"
                          placeholder="Enter country"
                          value={formData.address_country}
                          onChange={(e) => updateField('address_country', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Row className="g-2">
                        <Col sm={6}>
                          <Button
                            type="button"
                            variant="light"
                            className="w-100 border"
                            onClick={() => setStep(1)}
                            disabled={loading}
                          >
                            <i className="ri-arrow-left-line align-middle me-1" />
                            Back
                          </Button>
                        </Col>
                        <Col sm={6}>
                          <Button
                            className="btn btn-primary w-100"
                            type="submit"
                            disabled={loading || status === 'loading'}
                          >
                            {(loading || status === 'loading') && (
                              <Spinner size="sm" className="me-2" />
                            )}
                            Sign Up
                          </Button>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                )}
              </Form>

              <div className="text-center mt-4">
                <p className="mb-0">
                  Already have an account?{' '}
                  <Link
                    to="/login"
                    className="fw-semibold text-secondary text-decoration-underline"
                  >
                    Sign In
                  </Link>
                </p>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </ParticlesAuth>
  );
};

export default Register;
